import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { applyBrandColor } from "../../components/ThemeLoader";
import { invalidateBusinessSettings } from "../../hooks/useBusinessSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERMEDIARY_CFG = {
  agency: {
    key: "agencies", prefix: "agency", label: "Agência", title: "Agências (até 3)",
    desc: "Configure até 3 agências com nome e percentagem de comissão. No simulador, o consultor escolhe qual agência aplicar.",
    note: "A percentagem é retirada da rate do cliente e aparece em Custos.",
    off: "desactiva a agência",
  },
  partner: {
    key: "partners", prefix: "partner", label: "Parceiro", title: "Parceiros (até 3)",
    desc: "Configure até 3 parceiros com nome e percentagem de comissão. No simulador, escolhe-se Agência OU Parceiro (nunca os dois).",
    note: "A percentagem é retirada da rate do cliente e aparece em Custos (só custo empresa — não altera o líquido do colaborador).",
    off: "desactiva o parceiro",
  },
};

const IntermediaryConfig = ({ data, setData, kind }) => {
  const c = INTERMEDIARY_CFG[kind];
  const list = (data[c.key] && data[c.key].length === 3) ? data[c.key] : [1, 2, 3].map((n) => ({ id: `${c.prefix}_${n}`, name: `${c.label} ${n}`, fee_pct: 0 }));
  const update = (idx, patch) => setData({ ...data, [c.key]: list.map((a, i) => (i === idx ? { ...a, ...patch } : a)) });
  return (
    <div className="p-4 rounded-lg border bg-orange-50/40 mt-4" data-testid={`${kind}-config`}>
      <Label className="font-semibold text-orange-900">{c.title}</Label>
      <p className="text-xs text-muted-foreground mt-1 mb-3">{c.desc}<br />{c.note}</p>
      <div className="space-y-2">
        {list.map((ag, idx) => (
          <div key={ag.id || idx} className="flex items-center gap-2" data-testid={`${kind}-row-${idx}`}>
            <Input type="text" value={ag.name || ""} onChange={(e) => update(idx, { name: e.target.value })}
              placeholder={`${c.label} ${idx + 1}`} className="flex-1 font-mono" data-testid={`${kind}-name-${idx}`} />
            <div className="relative w-28">
              <Input type="number" min="0" max="100" step="0.5" value={ag.fee_pct ?? 0}
                onChange={(e) => update(idx, { fee_pct: parseFloat(e.target.value) || 0 })}
                className="pr-8 font-mono" data-testid={`${kind}-fee-pct-${idx}`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          </div>
        ))}
        <p className="text-[10px] text-slate-500 mt-2">Definir a taxa a <strong>0%</strong> {c.off} (não aparece no simulador).</p>
      </div>
    </div>
  );
};

const AdminBusinessSettings = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ iht_subject_to_irs: true, perm_operational_cost_pct: 10.0, working_days_month: 22 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/business-settings`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/business-settings`, data, { headers: getAuthHeaders() });
      setData(r.data);
      // Invalida o cache partilhado para que ThemeLoader/AgencySelector/snapshot_colors
      // vejam as novas configurações sem exigir reload da página.
      invalidateBusinessSettings();
      // Apply theme color live (no reload required)
      if (r.data?.theme_primary_color) applyBrandColor(r.data.theme_primary_color);
      toast.success("Configurações guardadas!");
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">A carregar...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-business-settings-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-700" />
              <span className="font-bold text-lg">Configurações do Negócio</span>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] text-white" data-testid="save-business-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "A guardar..." : "Guardar"}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">Tributação fiscal</h2>
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-blue-50/40">
              <div className="flex-1">
                <Label htmlFor="iht-toggle" className="font-semibold text-blue-900">
                  IHT sujeito a IRS
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Conforme Código do IRS, o IHT é rendimento da Categoria A e deve ser tributado integralmente.
                  Desligar apenas se a sua política contabilística reportar o IHT como ajudas de custo isentas
                  (prática comum em outsourcing IT, mas com risco fiscal).
                </p>
                <p className="text-[11px] mt-2 font-mono">
                  {data.iht_subject_to_irs
                    ? "IRS aplicado sobre 100% do bruto (Base + IHT)"
                    : "IRS aplicado apenas sobre 80% (Base) — IHT isento"}
                </p>
              </div>
              <Switch
                id="iht-toggle"
                checked={!!data.iht_subject_to_irs}
                onCheckedChange={(v) => setData({ ...data, iht_subject_to_irs: v })}
                data-testid="iht-subject-to-irs-switch"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">Dias úteis / mês</h2>
            <div className="p-4 rounded-lg border bg-amber-50/40">
              <Label htmlFor="working-days" className="font-semibold text-amber-900">
                Dias úteis por mês
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Usado para converter Valor/Hora e Valor/Dia em Valor/Mês nos regimes B2B e Recibos Verdes.
                Valores típicos: <strong>22</strong> (média mensal aproximada PT) ou <strong>21,7</strong> (média anual rigorosa).
                Este valor é usado tanto no simulador como no PDF da proposta — alterar globalmente alinha widget e PDF.
              </p>
              <div className="relative w-40">
                <Input
                  id="working-days"
                  type="number"
                  min="15" max="31" step="0.1"
                  value={data.working_days_month ?? 22}
                  onChange={(e) => setData({ ...data, working_days_month: parseFloat(e.target.value) || 22 })}
                  className="pr-12 font-mono"
                  data-testid="working-days-month-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">dias</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">Welcome Kit</h2>
            <div className="p-4 rounded-lg border bg-violet-50/40">
              <p className="text-xs text-muted-foreground mb-3">
                Custo único da empresa por novo colaborador (ex.: equipamento de boas-vindas, merchandise),
                diluído nos primeiros meses. <strong>Não aparece ao colaborador</strong> — apenas impacta a margem da empresa.
                No simulador, marcar o checkbox <em>&quot;Incluir Welcome Kit&quot;</em> para aplicar.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="wk-value" className="font-semibold text-violet-900">
                    Valor total (€)
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="wk-value"
                      type="number"
                      min="0" step="10"
                      value={data.welcome_kit_value ?? 0}
                      onChange={(e) => setData({ ...data, welcome_kit_value: parseFloat(e.target.value) || 0 })}
                      className="pr-8 font-mono"
                      data-testid="welcome-kit-value-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="wk-months" className="font-semibold text-violet-900">
                    Diluição (meses)
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="wk-months"
                      type="number"
                      min="1" max="24" step="1"
                      value={data.welcome_kit_months ?? 6}
                      onChange={(e) => setData({ ...data, welcome_kit_months: parseInt(e.target.value) || 6 })}
                      className="pr-12 font-mono"
                      data-testid="welcome-kit-months-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">m</span>
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.welcome_kit_apply_perm ?? true}
                  onChange={(e) => setData({ ...data, welcome_kit_apply_perm: e.target.checked })}
                  className="w-4 h-4 accent-violet-600"
                  data-testid="welcome-kit-apply-perm-input"
                />
                <span className="text-xs text-violet-900">
                  Aplicar também em <strong>Perm</strong> (fee one-shot — valor total deduzido da margem)
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="p-4 rounded-lg border bg-emerald-50/40">
              <Label htmlFor="op-cost-pct" className="font-semibold text-emerald-900">
                Custos Operacionais Perm (%)
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Percentagem aplicada ao Fee Total Perm para apurar os custos operacionais da empresa.
                Não é visível no simulador — só configurável aqui no backoffice. Por defeito: 10%.
              </p>
              <div className="relative w-40">
                <Input
                  id="op-cost-pct"
                  type="number"
                  min="0" max="100" step="0.1"
                  value={data.perm_operational_cost_pct ?? 10}
                  onChange={(e) => setData({ ...data, perm_operational_cost_pct: parseFloat(e.target.value) || 0 })}
                  className="pr-8 font-mono"
                  data-testid="perm-op-cost-pct-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <div className="p-4 rounded-lg border bg-amber-50/40 mt-4">
              <Label htmlFor="meal-overhead-pct" className="font-semibold text-amber-900">
                Overhead Cartão Refeição / Sub. Alimentação (%)
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Percentagem adicional ao custo do subsídio de alimentação atribuída à empresa
                (ex.: gestão do cartão, IVA não recuperável). Aplicada após o valor diluído do cartão.
                <br />Default: <strong>3%</strong>.
              </p>
              <div className="relative w-40">
                <Input
                  id="meal-overhead-pct"
                  type="number"
                  min="0" max="100" step="0.1"
                  value={data.meal_allowance_company_overhead_pct ?? 3}
                  onChange={(e) => setData({ ...data, meal_allowance_company_overhead_pct: parseFloat(e.target.value) || 0 })}
                  className="pr-8 font-mono"
                  data-testid="meal-overhead-pct-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <IntermediaryConfig data={data} setData={setData} kind="agency" />
            <IntermediaryConfig data={data} setData={setData} kind="partner" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6" data-testid="rv-entity-config">
            <h2 className="font-semibold text-lg mb-4">Recibos Verdes — SS Entidade Contratante</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Art. 168.º do Código Contributivo: a empresa paga uma % sobre o valor pago ao trabalhador quando é responsável por mais de 50% do rendimento dele.
              Não se aplica no 1.º ano isento nem abaixo de 6 × IAS/ano. Só custo empresa (não altera o líquido).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
              {[["rv_entity_rate_50_80", "Dependência 50–80% (%)", 7], ["rv_entity_rate_over_80", "Dependência > 80% (%)", 10]].map(([k, label, def]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <div className="relative">
                    <Input type="number" min="0" max="30" step="0.5" className="pr-8 font-mono"
                      value={data[k] ?? def}
                      onChange={(e) => setData({ ...data, [k]: parseFloat(e.target.value) || 0 })}
                      data-testid={k.replace(/_/g, "-")} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Lei 2026: {def}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">B2B — IRC + Derrama (PT 2026)</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Configuração alinhada com OE 2026 (Lei 45/2025). Aplica-se ao regime <strong>B2B Sociedade</strong>.
              <br />
              <strong>PME</strong>: taxa reduzida sobre os primeiros N€ de matéria coletável (default 50.000€).
              <strong>Geral</strong>: taxa sobre o remanescente. <strong>Derrama Municipal</strong>: até 1,5% sobre o lucro tributável.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">IRC PME (%)</Label>
                <div className="relative">
                  <Input type="number" min="0" max="50" step="0.5" className="pr-8 font-mono"
                    value={data.irc_pme_rate ?? 16}
                    onChange={(e) => setData({ ...data, irc_pme_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="irc-pme-rate" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">2026: 16%</p>
              </div>
              <div>
                <Label className="text-xs">IRC Geral (%)</Label>
                <div className="relative">
                  <Input type="number" min="0" max="50" step="0.5" className="pr-8 font-mono"
                    value={data.irc_general_rate ?? 20}
                    onChange={(e) => setData({ ...data, irc_general_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="irc-general-rate" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">2026: 20%</p>
              </div>
              <div>
                <Label className="text-xs">Limiar PME (€)</Label>
                <div className="relative">
                  <Input type="number" min="0" step="1000" className="pr-8 font-mono"
                    value={data.irc_pme_threshold ?? 50000}
                    onChange={(e) => setData({ ...data, irc_pme_threshold: parseFloat(e.target.value) || 0 })}
                    data-testid="irc-pme-threshold" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Lei: 50.000€</p>
              </div>
              <div>
                <Label className="text-xs">Derrama Municipal (%)</Label>
                <div className="relative">
                  <Input type="number" min="0" max="1.5" step="0.1" className="pr-8 font-mono"
                    value={data.derrama_municipal_pct ?? 1.5}
                    onChange={(e) => setData({ ...data, derrama_municipal_pct: parseFloat(e.target.value) || 0 })}
                    data-testid="derrama-pct" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Máx legal: 1,5%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">Cor da Marca</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Cor primária aplicada a toda a plataforma (botões, headers, ícones, links). Aplica-se imediatamente após guardar.
              <br />
              <span className="text-[11px]">Default Evolutt: <code className="font-mono">#0A2828</code></span>
            </p>
            <div className="flex items-center gap-4 max-w-lg">
              <div
                className="w-20 h-20 rounded-lg border shadow-sm shrink-0"
                style={{ backgroundColor: data.theme_primary_color || "#0A2828" }}
              />
              <div className="flex-1 space-y-2">
                <Label className="text-xs uppercase tracking-wide">Hex</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={data.theme_primary_color || "#0A2828"}
                    onChange={(e) => setData({ ...data, theme_primary_color: e.target.value })}
                    className="h-10 w-16 p-1 cursor-pointer"
                    data-testid="theme-primary-color-picker"
                  />
                  <Input
                    type="text"
                    value={data.theme_primary_color || "#0A2828"}
                    onChange={(e) => setData({ ...data, theme_primary_color: e.target.value })}
                    placeholder="#0A2828"
                    className="font-mono uppercase"
                    maxLength={7}
                    data-testid="theme-primary-color-hex"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs rounded border hover:bg-slate-50"
                    onClick={() => setData({ ...data, theme_primary_color: "#0A2828" })}
                    data-testid="theme-primary-reset"
                  >
                    Repor
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Tonalidades 500 (mais clara) e 700 (mais escura) são derivadas automaticamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4">Cores do Snapshot Negócio</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Cores dos cartões Receita, Custo Total e Fluidez no widget Snapshot Negócio.
              A cor da Margem é dinâmica (segue os escalões configurados em Margens).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: 'receita', label: 'Receita', defaults: { bg: '#dbeafe', text: '#1d4ed8' } },
                { key: 'custo', label: 'Custo Total', defaults: { bg: '#f3f4f6', text: '#374151' } },
                { key: 'fluidez', label: 'Fluidez', defaults: { bg: '#f1f5f9', text: '#334155' } },
              ].map(({ key, label, defaults }) => {
                const bgVal = data.snapshot_colors?.[`${key}_bg`] || defaults.bg;
                const textVal = data.snapshot_colors?.[`${key}_text`] || defaults.text;
                return (
                  <div key={key} className="p-3 rounded-lg border" style={{ backgroundColor: bgVal }}>
                    <Label className="font-semibold text-xs uppercase tracking-wide" style={{ color: textVal }}>
                      {label} (preview)
                    </Label>
                    <div className="font-bold mt-1 text-sm font-mono" style={{ color: textVal }}>
                      25 000,00 €
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <Label className="text-[10px]">Fundo</Label>
                        <Input
                          type="color"
                          value={bgVal}
                          onChange={(e) => setData({
                            ...data,
                            snapshot_colors: { ...(data.snapshot_colors || {}), [`${key}_bg`]: e.target.value },
                          })}
                          className="h-8 p-1 cursor-pointer"
                          data-testid={`snapshot-${key}-bg-input`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Texto</Label>
                        <Input
                          type="color"
                          value={textVal}
                          onChange={(e) => setData({
                            ...data,
                            snapshot_colors: { ...(data.snapshot_colors || {}), [`${key}_text`]: e.target.value },
                          })}
                          className="h-8 p-1 cursor-pointer"
                          data-testid={`snapshot-${key}-text-input`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminBusinessSettings;
